import { useState } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import Podcast from "../Components/Podcast";
import apiFetch from "../api";
import { useDocument } from "../hooks/useDocument";
import formatDate from "../utils/formatDate";

const buildPodcastPreview = (script) => {
  if (!script) return "";
  const firstLine = script.split("\n").find((line) => line.trim().length > 0);
  return firstLine || script.substring(0, 200);
};

const Podcasts = () => {
  const [page, setPage] = useState(1);

  const { data: podcasts, isLoading } = useQuery(
    ["podcasts", page],
    async () => {
      const search = new URLSearchParams({
        ordering: "-created_at",
        page: page.toString(),
      }).toString();
      return await apiFetch(`/api/podcasts/?${search}`);
    },
    { keepPreviousData: true }
  );

  useDocument({ title: "Podcasts" });

  const handlePrev = () => setPage((prev) => Math.max(1, prev - 1));
  const handleNext = () => setPage((prev) => prev + 1);

  return (
    <div className="m-auto max-w-4xl">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h1>Podcasts</h1>
          <p className="text-gray-600">
            Browse every generated episode, listen inline, or open the full story list.
          </p>
          {podcasts?.count !== undefined && (
            <p className="text-sm text-gray-500">
              Showing {podcasts?.results?.length || 0} of {podcasts.count} episodes
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
            disabled={!podcasts?.previous}
            onClick={handlePrev}
          >
            Previous
          </button>
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
            disabled={!podcasts?.next}
            onClick={handleNext}
          >
            Next
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-600">Loading podcasts...</div>}

      {!isLoading && podcasts?.results?.length === 0 && (
        <div className="rounded border border-dashed border-gray-300 p-6 text-gray-500">
          No podcasts have been generated yet. Create one to see it here.
        </div>
      )}

      <div className="space-y-4">
        {podcasts?.results?.map((podcast) => {
          const preview = buildPodcastPreview(podcast.script);
          return (
            <div
              key={podcast.id}
              className="rounded-lg border border-gray-200 p-4 shadow-sm"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs uppercase text-gray-500">Episode</p>
                  <h2 className="text-xl font-semibold">
                    {podcast.title || "Untitled episode"}
                  </h2>
                  <p className="text-gray-500">
                    {formatDate(podcast.date || podcast.created_at)}
                  </p>
                </div>
                <Link
                  to={`/news/${podcast.id}`}
                  className="text-primary-600 font-medium hover:underline"
                >
                  View details
                </Link>
              </div>

              <div className="mt-3">
                <Podcast id={podcast.id} />
              </div>

              {preview && (
                <p className="mt-3 text-sm text-gray-700">
                  {preview}
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
                <span>{podcast.articles?.length || 0} stories</span>
                {podcast.stories?.length !== undefined && (
                  <span>{podcast.stories.length} story bundles</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Podcasts;
